import { useEffect } from 'react'
import { utils } from 'ethers'
import { useTranslation } from 'react-i18next'
import {
    BuyNodeV2,
    NodeData,
    ApprovesUSDT,
    Claim,
    ClaimNode,
    ClaimCVC,
    StairData,
    SetOrder,
    ClaimStair,
    ApprovesAll,
    BuyWithCVCDOrder,
    BuyWithUSDTOrder,
    CvcdSwapData,
    ApprovesCVCForCvcdSwap,
    ApprovesCVCDForCvcdSwap,
    StakeCvcForCvcdSwap,
    ExchangeCvcdByStakeQuota,
    ClaimCvcdSwapOrder,
    ReleaseAllCvcdSwapUSDT
} from '../../chain/v2/ma'

import {
    EtherToWei,
    ZERO_ADDRESS
} from '../../ethers/e'
import {
    useEthers,
    useBlockCall
} from '../../ethers'

import {
    isETHAddress,
    getUrlParams,
} from '../../utils'

import {
    Error
} from '../tools'

import useButtonText from '../useButtonText'
import useInput from '../useInput'
import useToast from '../useToast'

export function useStairData() {
    return useBlockCall(
        account => {
            return StairData(account)
        },
        StairData.Type
    )
}

export function useNodeData() {
    return useBlockCall(
        account => {
            return NodeData(account)
        },
        NodeData.Type
    )
}

// CVCD_SWAP_JS_START: hooks for CvcdSwap page data and actions.
export function useCvcdSwapData() {
    return useBlockCall(
        account => {
            return CvcdSwapData(account)
        },
        CvcdSwapData.Type
    )
}
// CVCD_SWAP_JS_END: hooks for CvcdSwap page data and actions.

export function useBuyNode(usdtInvested = 0) {
    const {t} = useTranslation()
    const sponsorInput = useInput("")
    const hashInput = useInput("")
    const amountInput = useInput("", {type:'number'})
    const {open} = useToast()
    const {
        button,
        loadingButton,
        initButton,
    } = useButtonText(t('Buy Node'))

    useEffect(() => {
        const {r} = getUrlParams()
        if (r) sponsorInput.onChange(r)
    }, [])

    button.onClick = async () => {
        const sponsor = sponsorInput.value === "" ? ZERO_ADDRESS : sponsorInput.value
        if (!isETHAddress(sponsor)) {
            open(t("sponsor address error"), "error")
            return
        }
        let amount
        let invested
        try {
            amount = utils.parseUnits(String(amountInput.value || 0), 18)
            invested = utils.parseUnits(String(usdtInvested || 0), 18)
        } catch (e) {
            open(t("amount error"), "error")
            return
        }
        const total = amount.add(invested)
        if (amount.lte(0)) {
            open(t("amount error"), "error")
            return
        }
        if (invested.gte(utils.parseUnits("3000", 18))) {
            open(t("node investment is already full"), "error")
            return
        }
        if (total.lt(utils.parseUnits("300", 18))) {
            open(t("minimum node investment is 300 USDT"), "error")
            return
        }
        if (total.gt(utils.parseUnits("3000", 18))) {
            open(t("maximum node investment is 3000 USDT"), "error")
            return
        }
        loadingButton(t('Pending'))
        try {
            loadingButton(t('Approve USDT'))
            let {
                tx,
                error
            } = await ApprovesUSDT()
            loadingButton(t('Pending'))
            if (error) {
                open(Error(error), "error")
            }
            else {
                await tx.wait()
                tx = await BuyNodeV2(amount, sponsor, hashInput.value)
                await tx.wait()
                amountInput.onChange("")
                open(t('Success'))
            }
        } catch (e) {
            console.log(e)
            open(Error(e), "error")
        }
        initButton()
    }

    return {
        button,
        hashInput,
        sponsorInput,
        amountInput
    }
}

export function useClaim() {
    const {t} = useTranslation()
    const {open} = useToast()
    const {account} = useEthers()
    const {
        button,
        loadingButton,
        initButton,
    } = useButtonText(t('Claim'))

    button.onClick = async () => {
        loadingButton(t('Pending'))
        try {
            const tx = await Claim(account)
            await tx.wait()
            open(t('Success'))
            initButton()
            return true
        } catch (e) {
            console.log(e)
            open(Error(e), "error")
            initButton()
            return false
        }
    }

    return button
}

export function useClaimNode() {
    const {t} = useTranslation()
    const {open} = useToast()
    const {account} = useEthers()
    const {
        button,
        loadingButton,
        initButton,
    } = useButtonText(t('Claim'))

    button.onClick = async () => {
        loadingButton(t('Pending'))
        try {
            const tx = await ClaimNode(account)
            await tx.wait()
            open(t('Success'))
            initButton()
            return true
        } catch (e) {
            console.log(e)
            open(Error(e), "error")
            initButton()
            return false
        }
    }

    return button
}

export function useClaimCVC() {
    const {t} = useTranslation()
    const {open} = useToast()
    const {account} = useEthers()
    const {
        button,
        loadingButton,
        initButton,
    } = useButtonText(t('Claim'))

    button.onClick = async () => {
        loadingButton(t('Pending'))
        try {
            const tx = await ClaimCVC(account)
            await tx.wait()
            open(t('Success'))
            initButton()
            return true
        } catch (e) {
            console.log(e)
            open(Error(e), "error")
            initButton()
            return false
        }
    }

    return button
}

export function useReInvestOrder(id) {
    const {t} = useTranslation()
    const {open} = useToast()
    const {
        button,
        loadingButton,
        initButton,
    } = useButtonText(t('Vip+1'))

    button.onClick = async () => {
        loadingButton(t('Pending'))
        try {
            const tx = await SetOrder(id, 1)
            await tx.wait()
            open(t('Success'))
            initButton()
            return true
        } catch (e) {
            console.log(e)
            open(Error(e), "error")
            initButton()
            return false
        }
    }

    return button
}

export function useWithdrawOrder(id) {
    const {t} = useTranslation()
    const {open} = useToast()
    const {
        button,
        loadingButton,
        initButton,
    } = useButtonText(t('Close'))

    button.onClick = async () => {
        loadingButton(t('Pending'))
        try {
            const tx = await SetOrder(id, 0)
            await tx.wait()
            open(t('Success'))
            initButton()
            return true
        } catch (e) {
            console.log(e)
            open(Error(e), "error")
            initButton()
            return false
        }
    }

    return button
}

export function useBuyOrder() {
    const {t} = useTranslation()
    const hashInput = useInput("")
    const sponsorInput = useInput("")
    const amountInput = useInput("", {type: 'number'})
    const {open} = useToast()

    const {
        button,
        loadingButton,
        initButton,
    } = useButtonText(t('USDT Buy'))

    const {
        button: buttonCVCD,
        loadingButton: loadingButtonCVCD,
        initButton: initButtonCVCD,
    } = useButtonText(t('CVCD + USDT'))

    useEffect(() => {
        const {r} = getUrlParams()
        if (r) sponsorInput.onChange(r)
    }, [])

    buttonCVCD.onClick = async () => {
        if (!isETHAddress(sponsorInput.value)) {
            open(t("sponsor address error"), "error")
            return
        }
        loadingButtonCVCD(t('Pending'))
        try {
            loadingButtonCVCD(t('Approve...'))
            let {
                tx,
                error
            } = await ApprovesAll()
            loadingButtonCVCD(t('Pending'))
            if (error) {
                open(Error(error), "error")
            }
            else {
                await tx.wait()
                tx = await BuyWithCVCDOrder(EtherToWei(amountInput.value / 2), sponsorInput.value, hashInput.value)
                await tx.wait()
                open(t('Success'))
            }
        } catch (e) {
            console.log(e)
            open(Error(e), "error")
        }
        initButtonCVCD()
    }

    button.onClick = async () => {
        if (!isETHAddress(sponsorInput.value)) {
            open(t("sponsor address error"), "error")
            return
        }
        loadingButton(t('Pending'))
        try {
            loadingButton(t('Approve USDT'))
            let {
                tx,
                error
            } = await ApprovesUSDT()
            loadingButton(t('Pending'))
            if (error) {
                open(Error(error), "error")
            }
            else {
                await tx.wait()
                tx = await BuyWithUSDTOrder(EtherToWei(amountInput.value), sponsorInput.value, hashInput.value)
                await tx.wait()
                open(t('Success'))
            }
        } catch (e) {
            console.log(e)
            open(Error(e), "error")
        }
        initButton()
    }

    return {
        button,
        buttonCVCD,
        hashInput,
        amountInput,
        sponsorInput
    }
}

export function useSwap() {
    const {t} = useTranslation()
    const amountInput = useInput("", {type: 'number'})
    const {open} = useToast()

    const {
        button,
        loadingButton,
        initButton,
    } = useButtonText(t('Swap to USDT'))

    button.onClick = async () => {
        loadingButton(t('Pending'))
        try {
            loadingButton(t('Approve CVCD'))
            let {
                tx,
                error
            } = await ApprovesCVCDForCvcdSwap()
            loadingButton(t('Pending'))
            if (error) {
                open(Error(error), "error")
            }
            else {
                await tx.wait()
                tx = await ExchangeCvcdByStakeQuota(EtherToWei(amountInput.value))
                await tx.wait()
                amountInput.onChange("")
                open(t('Success'))
            }
        } catch (e) {
            console.log(e)
            open(Error(e), "error")
        }
        initButton()
    }

    return {
        button,
        amountInput
    }
}

// CVCD_SWAP_JS_START: hooks for CvcdSwap stake and claim actions.
export function useStakeCvcForCvcdSwap() {
    const {t} = useTranslation()
    const amountInput = useInput("", {type: 'number'})
    const {open} = useToast()
    const {
        button,
        loadingButton,
        initButton,
    } = useButtonText(t('Stake'))

    button.onClick = async () => {
        const amount = amountInput.value * 1
        if (!amount || amount <= 0) {
            open(t("amount error"), "error")
            return
        }
        loadingButton(t('Pending'))
        try {
            loadingButton(t('Approve CVC'))
            let {
                tx,
                error
            } = await ApprovesCVCForCvcdSwap()
            loadingButton(t('Pending'))
            if (error) {
                open(Error(error), "error")
            }
            else {
                await tx.wait()
                tx = await StakeCvcForCvcdSwap(EtherToWei(amountInput.value))
                await tx.wait()
                amountInput.onChange("")
                open(t('Success'))
            }
        } catch (e) {
            console.log(e)
            open(Error(e), "error")
        }
        initButton()
    }

    return {
        button,
        amountInput
    }
}

export function useClaimCvcdSwapOrder(orderId) {
    const {t} = useTranslation()
    const {open} = useToast()
    const {
        button,
        loadingButton,
        initButton,
    } = useButtonText(t('Claim'))

    button.onClick = async () => {
        loadingButton(t('Pending'))
        try {
            const tx = await ClaimCvcdSwapOrder(orderId)
            await tx.wait()
            open(t('Success'))
            initButton()
            return true
        } catch (e) {
            console.log(e)
            open(Error(e), "error")
            initButton()
            return false
        }
    }

    return button
}

export function useClaimCvcdSwapAll() {
    const {t} = useTranslation()
    const {open} = useToast()
    const {account} = useEthers()
    const {
        button,
        loadingButton,
        initButton,
    } = useButtonText(t('Claim'))

    button.onClick = async () => {
        loadingButton(t('Pending'))
        try {
            const tx = await ClaimCvcdSwapOrder(account)
            await tx.wait()
            open(t('Success'))
            initButton()
            return true
        } catch (e) {
            console.log(e)
            open(Error(e), "error")
            initButton()
            return false
        }
    }

    return button
}

export function useReleaseCvcdSwapUSDTAll() {
    const {t} = useTranslation()
    const {open} = useToast()
    const {account} = useEthers()
    const {
        button,
        loadingButton,
        initButton,
    } = useButtonText(t('Claim'))

    button.onClick = async () => {
        loadingButton(t('Pending'))
        try {
            const tx = await ReleaseAllCvcdSwapUSDT(account)
            await tx.wait()
            open(t('Success'))
            initButton()
            return true
        } catch (e) {
            console.log(e)
            open(Error(e), "error")
            initButton()
            return false
        }
    }

    return button
}
// CVCD_SWAP_JS_END: hooks for CvcdSwap stake and claim actions.

export function useClaimStair() {
    const {t} = useTranslation()
    const {open} = useToast()
    const {account} = useEthers()
    const {
        button,
        loadingButton,
        initButton,
    } = useButtonText(t('Claim'))

    button.onClick = async () => {
        loadingButton(t('Pending'))
        try {
            const tx = await ClaimStair(account)
            await tx.wait()
            open(t('Success'))
            initButton()
            return true
        } catch (e) {
            console.log(e)
            open(Error(e), "error")
            initButton()
            return false
        }
    }

    return button
}
